import React, { ReactElement, useState } from "react";
import { useMutation } from "@apollo/client";
import {
  FormMessage,
  Label,
  Select,
  Text,
  TextArea,
} from "@xometry/xometry_loft";
import { useContextSelector } from "use-context-selector";
import {
  UpsertProposalCancellationTermsQuery,
  ProposalCancellationTerms,
} from "../../../schema/generated/schema";
import Modal from "../Common/Modal";
import { ModalInputContainer, ModalStyles } from "./styles";
import ProposalContext from "../contexts/proposalContext";

const reasonsForClosing = [
  "I am not interested in doing this work.",
  "I do not have the capabilities to do some or all of this work.",
  "I do not have the available capacity to do this work.",
  "Spam",
  "Offensive",
  "Other",
];

interface CancelModalProps {
  proposalId: string;
  modalOpen: boolean;
  setModal: (boolean) => void;
  onSubmit?: () => void;
  cancellationTerms?: ProposalCancellationTerms;
}

const CancelModal = ({
  proposalId,
  modalOpen,
  setModal,
  onSubmit,
  cancellationTerms,
}: CancelModalProps): ReactElement => {
  const [formData, setFormData] = useState({
    reasonClosed: cancellationTerms?.reasonClosed || "",
    id: cancellationTerms?.id || "",
  });
  const refetch = useContextSelector(
    ProposalContext,
    context => context.actions?.refetch
  );
  const [errors, setErrors] = useState({ reasonClosed: false });

  const [upsertProposalCancellationTerms] = useMutation(
    UpsertProposalCancellationTermsQuery
  );

  const saveData = (name: string, value: string | number | boolean) => {
    if (errors[name]) {
      setErrors({ ...errors, [name]: false });
    }
    setFormData(currentValue => ({ ...currentValue, [name]: value }));
  };

  const handleSave = async () => {
    await upsertProposalCancellationTerms({
      variables: {
        proposalId,
        cancellationTerms: formData,
      },
    });
  };

  const validateForm = () => {
    if (formData.reasonClosed === "") {
      setErrors({ ...errors, reasonClosed: true });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    try {
      await handleSave();
      onSubmit?.();
      setModal(false);
      if (refetch) {
        refetch();
      }
    } catch (err) {
      console.error(err, "failed validation");
    }
  };

  return (
    <Modal
      isOpen={modalOpen}
      selector="#modal-mount"
      title={<Text kind="panel-title">Decline project?</Text>}
      description={
        <Text>
          Declining this project will inform the customer that you are not
          interested in providing a quote at this time.
        </Text>
      }
      submitActionText="Decline Request"
      submitButtonType="primary-thomas"
      handleSubmit={handleSubmit}
      handleCloseButton={() => {
        setModal(false);
      }}
      handleCancel={() => {
        setModal(false);
      }}
      cssStyle={ModalStyles}
      buttonScale="large"
    >
      <ModalInputContainer>
        <Label className="reason-for-canceling--label">
          Reason for Declining
        </Label>
        <Select
          name="reasonClosed"
          onChange={({ target }) => saveData(target.name, target.value)}
          className="reason-canceling--select"
          scale="large"
          defaultValue=""
          required
          invalid={errors.reasonClosed}
        >
          <option value="" disabled>
            Select an option...
          </option>
          {reasonsForClosing.map(item => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>
        {errors.reasonClosed && (
          <FormMessage invalid>Reason for Closing is required.</FormMessage>
        )}
      </ModalInputContainer>

      <ModalInputContainer>
        <Label className="additional-comments--label">
          Additional Comments <span>Optional</span>
        </Label>
        <TextArea
          className="additional-notes--textarea"
          scale="large"
          name="additionalNotes"
          onBlur={({ target }) => saveData(target.name, target.value)}
        />
      </ModalInputContainer>
    </Modal>
  );
};

CancelModal.defaultProps = {
  onSubmit: undefined,
  cancellationTerms: {
    id: "",
  },
};

export default CancelModal;
